import React, {useState} from 'react'
import TimePicker from 'react-time-picker'
import DatePicker from 'react-datepicker'
import * as Yup from 'yup'
import styles from '../LeadManagementStyles/LeadManagementStyle.module.css'
import {FormikValues, useFormik} from 'formik'
import {scheduleVisit} from '../LeadManagementAPI'
import {scheduleVisitDataType} from '../LeadManagementDataTypes'

const ScheduleVisit = ({
  setSaveFeedback,
  setScheduleVisit,
  leads_property_uuid,
  getLeadList,
  scheduled_visit_date,
  setSuccessMsg,
  setError,
}: scheduleVisitDataType) => {
  const [startTime, setStartTime] = useState<string | null>(
    scheduled_visit_date
      ? new Date(scheduled_visit_date).toLocaleTimeString('en-US', {hour12: false})
      : null
  )
  const [startDate, setStartDate] = useState<Date | null>(
    scheduled_visit_date ? new Date(scheduled_visit_date) : null
  )
  const [scheduleBtnLoading, setScheduleBtnLoading] = useState<boolean>(false)

  // function to format date
  const formatDate = (date: Date) => {
    const formattedDate = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formattedDate
  }

  // initial values for schedule visit
  const initialValues = {
    scheduleDate: scheduled_visit_date ? formatDate(new Date(scheduled_visit_date)) : '',
    scheduleTime: scheduled_visit_date
      ? new Date(scheduled_visit_date).toLocaleTimeString('en-US', {hour12: false})
      : '',
  }

  // Validation schema for schedule visit
  const validationSchema = Yup.object().shape({
    scheduleDate: Yup.string().required('Date is required'),
    scheduleTime: Yup.string().required('Time is required').typeError('Time is required'),
  })

  // function for schedule visit
  const handleScheduleVisit = async (values: FormikValues) => {
    setScheduleBtnLoading(true)
    try {
      const {data: response} = await scheduleVisit(
        leads_property_uuid,
        `${values.scheduleDate} ${values.scheduleTime}`
      )
      if (response.code === '200') {
        getLeadList()
        setScheduleVisit(null)
        setSuccessMsg(`Success: Visit Scheduled Successfully!`)
      } else {
        setError(`Error: ${response.msg_details}`)
      }
    } catch (error: any) {
      setError(`Error: ${error}`)
    } finally {
      setScheduleBtnLoading(false)
    }
  }

  // formik object for schedule visit
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => handleScheduleVisit(values),
  })
  return (
    <tr className='w-100 '>
      <td colSpan={5} className='pb-0'>
        <div className='d-flex justify-content-between'>
          {/* Begin: Heading */}
          <h4>Schedule a Visit</h4>
          {/* End: Heading */}
          {/* Begin: Close button */}
          <div
            className='btn btn-sm btn-icon btn-active-color-primary px-5'
            onClick={() => {
              setSaveFeedback(null)
              setScheduleVisit(null)
            }}
          >
            <i className='bi bi-x-lg'></i>
          </div>
          {/* End: Close button */}
        </div>
        {/* Begin : Form */}
        <form onSubmit={formik.handleSubmit}>
          <div className='row w-100'>
            {/* Begin: Form group Date & Time */}
            <div className={`fv-row mb-2 col-12 col-lg-10`}>
              <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                <span className='required'>Choose Date & Time</span>
                <i
                  className='fas fa-exclamation-circle ms-2 fs-7'
                  data-bs-toggle='tooltip'
                  title='Enter date & time'
                ></i>
              </label>
              <div className='row w-100'>
                {/* Begin: Date field */}
                <div className='col-6'>
                  <DatePicker
                    className='form-control form-control-md form-control-solid py-2 px-1 w-100'
                    autoComplete='off'
                    selected={startDate}
                    dateFormat='yyyy/MM/dd'
                    placeholderText='Choose date'
                    selectsStart
                    startDate={startDate}
                    minDate={new Date()}
                    onChange={(date: Date) => {
                      if (date === null) {
                        setStartDate(date)
                        formik.setFieldTouched('scheduleDate', true, true)
                        formik.setFieldValue('scheduleDate', '', true)
                      } else if (
                        new Date(
                          `1970-01-01T${new Date().toLocaleTimeString('en-US', {hour12: false})}`
                        ) > new Date(`1970-01-01T${formik.values.scheduleTime}`) &&
                        formatDate(new Date()) === formatDate(new Date(date))
                      ) {
                        setStartTime(null)
                        setStartDate(date)
                        formik.setFieldValue('scheduleDate', formatDate(new Date(date)), true)
                        formik.setFieldValue('scheduleTime', null, true)
                      } else {
                        setStartDate(date)
                        formik.setFieldValue('scheduleDate', formatDate(new Date(date)), true)
                      }
                    }}
                  />
                  <div className='h-20px'>
                    {formik.touched.scheduleDate && formik.errors.scheduleDate && (
                      <div className='fv-help-block color-red'>
                        {formik.errors.scheduleDate.toString()}
                      </div>
                    )}
                  </div>
                </div>
                {/* End: Date field */}
                {/* Begin: Time field */}
                <div className='col-6'>
                  <TimePicker
                    hourPlaceholder='hh'
                    minutePlaceholder='mm'
                    className={`form-control form-control-md form-control-solid py-2 px-1 ${styles.no_border}`}
                    onChange={(value) => {
                      formik.setFieldTouched('scheduleTime', true, true)
                      if (value === null) {
                        setStartTime(value)
                        formik.setFieldValue('scheduleTime', '')
                      } else if (
                        new Date(
                          `1970-01-01T${new Date().toLocaleTimeString('en-US', {hour12: false})}`
                        ) > new Date(`1970-01-01T${value}`) &&
                        formatDate(new Date()) === formatDate(new Date(formik.values.scheduleDate))
                      ) {
                        setStartTime(null)
                        formik.setFieldValue('scheduleTime', null, true)
                      } else {
                        setStartTime(value)
                        formik.setFieldValue('scheduleTime', `${value}:00`, true)
                      }
                    }}
                    value={startTime}
                  />
                  <div className='h-20px'>
                    {formik.touched.scheduleTime && formik.errors.scheduleTime && (
                      <div className='fv-help-block color-red'>{formik.errors.scheduleTime}</div>
                    )}
                  </div>
                </div>
                {/* Begin: Time field */}
              </div>
            </div>
            {/* End: Form group Date & Time */}
            <div className={`fv-row mb-2 col-12 col-lg-2`}>
              {/* Begin:: submit button */}
              <button
                type='submit'
                className='btn btn-lg btn-primary w-100 py-2'
                // data-kt-stepper-action='submit'
                disabled={scheduleBtnLoading}
              >
                {scheduleBtnLoading ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Processing...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  `${scheduled_visit_date ? 'Reschedule Visit' : 'Schedule Visit'}`
                )}
              </button>
              {/* Begin:: submit button */}
            </div>
          </div>
        </form>
        {/* Begin : Form */}
      </td>
    </tr>
  )
}

export default ScheduleVisit
